import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import Pagination from '@mui/material/Pagination';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import { checkPermission } from '../../../utils/checkPermissions';
import PermissionWrapper from '../../../components/PermissionWrapper';

export default function SMSTemplates() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const userPermissions = authCtx.permissions;

  // Check permission
  const canManageSmsTemplates = checkPermission(userPermissions, 'settings', 'manage_sms_templates');

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  });

  // Search/Filter states
  const [filters, setFilters] = useState({
    search: '',
    productId: ''
  });

  // Dropdown options
  const [products, setProducts] = useState([]);

  // Fetch SMS templates
  const fetchTemplates = async (page = 1, searchFilters = filters) => {
    try {
      setLoading(true);

      const response = await postRequest('private/get-sms-templates', {
        page,
        limit: 10,
        search: searchFilters.search,
        productId: searchFilters.productId || undefined
      }, authCtx.token);

      if (response?.status === 'success' || response?.status === 200) {
        setTemplates(response.data?.templates || []);
        setPagination(response.data?.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0
        });
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      const response = await postRequest('private/getproductList', {}, authCtx.token);
      if (response?.data) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    if (authCtx.token) {
      fetchTemplates();
      fetchProducts();
    }
  }, [authCtx.token]);

  // Handle search
  const handleSearch = () => {
    fetchTemplates(1, filters);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    fetchTemplates(value, filters);
  };

  // Handle input changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle delete template
  const handleDelete = async (templateId, templateName) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `This will delete the SMS template "${templateName}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await postRequest(`private/delete-sms-template/${templateId}`, {}, authCtx.token);

        if (response?.status === 'success' || response?.status === 200) {
          Swal.fire('Deleted!', 'SMS template has been deleted.', 'success');
          fetchTemplates(pagination.currentPage, filters);
        } else {
          throw new Error(response?.message || 'Failed to delete template');
        }
      } catch (error) {
        Swal.fire('Error', error.message || 'Failed to delete SMS template', 'error');
      }
    }
  };

  // Handle edit template
  const handleEdit = (templateId) => {
    navigate(`/sms-templates/edit/${templateId}`);
  };

  return (
    <PermissionWrapper
      category="settings"
      permission="manage_sms_templates"
      pageTitle="SMS Templates"
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'SMS Templates', path: '' },
      ]}
    >
      <>
        <div className='page-header pb-3'>
          <h1 className='mt-0 mb-0'>SMS Templates</h1>
          {canManageSmsTemplates && (
            <div className='header-right-btn'>
              <Stack spacing={2} direction="row">
                <Button
                  variant="outlined"
                  className='red-outline-btn text-capitalize'
                  onClick={() => navigate('/sms-templates/create')}
                >
                  <AddIcon /> &nbsp; Create SMS Template
                </Button>
              </Stack>
            </div>
          )}
        </div>

        {/* Search Form */}
        <div className='page-top-formview mb-3'>
          <Form>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="searchProduct">
                <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                  <InputLabel id="product-select-label">Product</InputLabel>
                  <Select
                    labelId="product-select-label"
                    id="product-select"
                    value={filters.productId}
                    label="Product"
                    onChange={(e) => handleFilterChange('productId', e.target.value)}
                  >
                    <MenuItem value="">All Products</MenuItem>
                    <MenuItem value="any">Any</MenuItem>
                    {products.map((product) => (
                      <MenuItem key={product._id} value={product._id}>
                        {product.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Form.Group>

              <Form.Group as={Col} controlId="searchName">
                <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                  <TextField
                    id="template-name-search"
                    label="Template Name"
                    variant="outlined"
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </FormControl>
              </Form.Group>

              <Form.Group as={Col} controlId="searchButton">
                <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                  <Stack spacing={2} direction="row">
                    <Button
                      variant="contained"
                      onClick={handleSearch}
                      className='red-bg red-button text-capitalize h-56'
                    >
                      <SearchIcon /> &nbsp; Search
                    </Button>
                  </Stack>
                </FormControl>
              </Form.Group>
            </Row>
          </Form>

          <div className='customer-tab-view'>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                <CircularProgress />
              </div>
            ) : (
              <>
                <Table striped responsive bordered>
                  <thead>
                    <tr>
                      <th colSpan={3} className='bg-grey'>SMS Templates</th>
                    </tr>
                    <tr>
                      <th>Product</th>
                      <th>Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.length === 0 ? (
                      <tr>
                        <td colSpan={3} style={{ textAlign: 'center', padding: '50px' }}>
                          No SMS templates found
                        </td>
                      </tr>
                    ) : (
                      templates.map((template) => (
                        <tr key={template._id}>
                          <td>{template.productId?.title || 'Any'}</td>
                          <td>
                            <strong>{template.name}</strong>
                            {template.message && (
                              <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                {template.message.substring(0, 100)}
                                {template.message.length > 100 ? '...' : ''}
                              </div>
                            )}
                          </td>
                          <td>
                            <Tooltip title="Edit Template">
                              <span
                                className="red-link-text cursor-pointer"
                                onClick={() => handleEdit(template._id)}
                              >
                                <EditOutlinedIcon />
                              </span>
                            </Tooltip>
                            &nbsp;&nbsp;&nbsp;
                            <Tooltip title="Delete Template">
                              <span
                                className='red-link-text cursor-pointer w-18'
                                onClick={() => handleDelete(template._id, template.name)}
                              >
                                <DeleteOutlineOutlinedIcon />
                              </span>
                            </Tooltip>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>

                {/* Pagination */}
                {pagination.totalCount > 10 && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                    <Pagination
                      count={pagination.totalPages}
                      page={pagination.currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      size="medium"
                      sx={{
                        '& .Mui-selected': {
                          backgroundColor: '#bb0000 !important',
                          color: '#fff !important',
                        },
                        '& .MuiPaginationItem-root': {
                          color: '#bb0000',
                          borderColor: '#bb0000',
                        },
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </>
    </PermissionWrapper>
  );
}
